// src/services/api.js
import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '/api', // This will proxy to localhost:3000 via Vite
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    const adminToken = localStorage.getItem('adminToken');
    
    // Use admin token for admin routes, student token for student routes
    if (config.url?.includes('/admin/') && adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid — only redirect if not already on the login page
      if (error.config.url?.includes('/admin/')) {
        localStorage.removeItem('adminToken');
        if (!window.location.pathname.startsWith('/admin/login')) {
          window.location.href = '/admin/login';
        }
      } else {
        localStorage.removeItem('authToken');
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (cin, password, totpCode) => {
    const response = await api.post('/auth/login', { cin, password, ...(totpCode ? { totpCode } : {}) });
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
    }
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('authToken');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },
  
  getToken: () => {
    return localStorage.getItem('authToken');
  }
};

// Shared photo URL helper — works for both student and admin tokens
export const getStudentPhotoUrl = (codEtu) => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken') || '';
  return `/api/photo/${codEtu}?token=${encodeURIComponent(token)}`;
};

// Student API
export const studentAPI = {
  getStudentPhotoUrl,
  getMenuConfig: async () => {
    const response = await api.get('/app/menu-config');
    return response.data;
  },

  // Password & 2FA
  changePassword: (currentPassword, newPassword) => api.post('/student/change-password', { currentPassword, newPassword }),
  get2FAStatus: () => api.get('/student/2fa/status'),
  setup2FA: () => api.post('/student/2fa/setup'),
  verify2FA: (code) => api.post('/student/2fa/verify', { code }),
  disable2FA: (code) => api.post('/student/2fa/disable', { code }),

  getProfile: async () => {
    const response = await api.get('/student/me');
    return response.data;
  },

  getInstitutionalEmail: async () => {
    const response = await api.get('/student/institutional-email');
    return response.data;
  },
  
  getStudentCardRequestStatus: async () => {
    const response = await api.get('/student/request/student-card/status');
    return response.data;
  },

  getValidatedModules: async () => {
    const response = await api.get('/student/validated-modules');
    return response.data;
  },
  
  getPedagogicalSituation: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.year) params.append('year', filters.year);
    
    const response = await api.get(`/student/pedagogical-situation?${params}`);
    return response.data;
  },
  
  requestStudentCard: (formData) => api.post('/student/request/student-card', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }).then(res => res.data),
  
  getPedagogicalStats: async () => {
    const response = await api.get('/student/pedagogical-stats');
    return response.data;
  },

  getAdministrativeSituation: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.year) params.append('year', filters.year);
    
    const response = await api.get(`/student/administrative-situation?${params}`);
    return response.data;
  },

  getAdministrativeStats: async () => {
    const response = await api.get('/student/administrative-stats');
    return response.data;
  },
  
  // Current year grades from RESULTAT_EPR
  getGrades: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.year) params.append('year', filters.year);
    if (filters.session) params.append('session', filters.session);
    
    const response = await api.get(`/student/grades?${params}`);
    return response.data;
  },
  getGradesYears: async () => {
  const response = await api.get('/student/grades/years');
  return response.data;
},
  // Official documents/transcripts 
  logDocumentDownload: async (doc_type, doc_ref) => {
    return api.post('/api/log-document-download', { doc_type, doc_ref }).catch(() => {});
  },
  getOfficialDocuments: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.semester) params.append('semester', filters.semester);
    
    const response = await api.get(`/student/official-documents?${params}`);
    return response.data;
  },
  
  // Generate transcript PDF (server-side)
  downloadTranscriptPDF: async (semester, studentCode) => {
    try {
      const response = await api.get(`/student/transcript/${semester}/pdf`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Releve_Notes_S${semester}_${studentCode}_${new Date().getFullYear()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Download PDF error:', error);
      throw error;
    }
  },
  
  // Print transcript
  printTranscriptPDF: async (semester) => {
    try {
      const response = await api.get(`/student/transcript/${semester}/print`);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(response.data);
      printWindow.document.close();
      
    } catch (error) {
      console.error('Print transcript error:', error);
      throw error;
    }
  },
  
  getGradeStats: async () => {
    const response = await api.get('/student/grade-stats');
    return response.data;
  },

  getDiplomaProgress: async () => {
    const response = await api.get('/student/diploma-progress');
    return response.data;
  },

  getMySchedule: async () => {
    const response = await api.get('/student/my-schedule');
    return response.data;
  },

  getExamConvocation: async () => {
    const response = await api.get('/student/convocation');
    return response.data;
  },

  // Grade reclamations
  getReclaimableGrades: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const response = await api.get(`/student/exam-session/reclaimable-grades${qs ? `?${qs}` : ''}`);
    return response.data;
  },
  getExamSessionResults: async () => {
    const response = await api.get('/student/exam-session/results');
    return response.data;
  },
  getRattrapageResults: async () => {
    const response = await api.get('/student/exam-session/rattrapage-results');
    return response.data;
  },
  getReclamations: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.status) params.append('status', filters.status);

    const response = await api.get(`/student/reclamations?${params}`);
    return response.data;
  },
  submitReclamation: async (data) => {
    const response = await api.post('/student/reclamations', data);
    return response.data;
  },
  getReclamationsSettings: async () => {
    const response = await api.get('/student/reclamations/settings');
    return response.data;
  },

  // Notifications
  getNotifications: async () => {
    const response = await api.get('/student/notifications');
    return response.data;
  },
  markNotificationRead: async (id) => {
    const response = await api.put(`/student/notifications/${id}/read`);
    return response.data;
  }
};

// Admin API
export const adminAPI = {
  login: async (username, password, totpCode) => {
    const response = await api.post('/admin/login', { username, password, ...(totpCode ? { totpCode } : {}) });
    if (response.data.token) {
      localStorage.setItem('adminToken', response.data.token);
    }
    return response.data;
  },
  get2FAStatus: () => api.get('/admin/2fa/status'),
  setup2FA:     () => api.post('/admin/2fa/setup'),
  verify2FA:    (code) => api.post('/admin/2fa/verify', { code }),
  disable2FA:   (code) => api.post('/admin/2fa/disable', { code }),
changePassword: async (currentPassword, newPassword) => {
  const response = await api.post('/admin/change-password', { 
    currentPassword, // This sends { currentPassword: "...", newPassword: "..." }
    newPassword 
  });
  return response.data;
},


  // Refresh Exam Participants — starts background job, returns { jobId }
  refreshExamParticipants: async () => {
    const response = await api.post('/admin/exams/refresh-participants');
    return response.data;
  },

  getRefreshParticipantsStatus: async (jobId) => {
    const response = await api.get(`/admin/exams/refresh-status/${jobId}`);
    return response.data;
  },
  // --- NOTIFICATIONS ---
  getNotifications: async () => {
    const response = await api.get('/admin/notifications');
    return response.data;
  },
  getNotificationsList: async () => {
    const response = await api.get('/admin/notifications/list');
    return response.data;
  },
  createNotification: async (data) => {
    const response = await api.post('/admin/notifications', data);
    return response.data;
  },
  deleteNotification: async (id) => {
    const response = await api.delete(`/admin/notifications/${id}`);
    return response.data;
  },
  deleteExpiredNotifications: async () => {
    const response = await api.delete('/admin/notifications/bulk/expired');
    return response.data;
  },
  revokeModules: async (studentCodes, moduleCodes) => {
    const response = await api.post('/admin/groups/revoke-modules', { studentCodes, moduleCodes });
    return response.data;
  },
  deleteOldNotifications: async (days) => {
    const response = await api.delete(`/admin/notifications/bulk/older?days=${days}`);
    return response.data;
  },
  deleteAllNotifications: async () => {
    const response = await api.delete('/admin/notifications/bulk/all');
    return response.data;
  },
  getNotificationFilieres: async () => {
    const response = await api.get('/admin/notifications/filieres');
    return response.data;
  },

  // Auth logs
  getAuthLogs: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/auth-logs?${q}`);
    return response.data;
  },

  // Documents
  getDocumentLogs: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/documents/logs?${q}`);
    return response.data;
  },
  getStudentInfoForDoc: async (cod_etu) => {
    const response = await api.get(`/admin/documents/student-info/${cod_etu}`);
    return response.data;
  },
  getStudentSituation: async (cod_etu) => {
    const response = await api.get(`/admin/students/${cod_etu}/situation`);
    return response.data;
  },

  // App settings
  getSettings: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },
  getDiplomas: async () => {
    const response = await api.get('/admin/settings/diplomas');
    return response.data;
  },
  updateSettings: async (updates) => {
    const response = await api.put('/admin/settings', { updates });
    return response.data;
  },

  getExamConflictsCount: async (sessionId) => {
    const params = sessionId ? `?session_id=${sessionId}` : '';
    const response = await api.get(`/admin/exams/conflicts/count${params}`);
    return response.data.count;
  },

  getExamConflictsDetails: async (sessionId) => {
    const params = sessionId ? `?session_id=${sessionId}` : '';
    const response = await api.get(`/admin/exams/conflicts/details${params}`);
    return response.data;
  },

  resetStudentPassword: async (codEtu, newPassword) => {
    const response = await api.post(`/admin/students/${codEtu}/reset-password`, { newPassword });
    return response.data;
  },

  disableStudent2FA: async (codEtu) => {
    const response = await api.post(`/admin/students/${codEtu}/disable-2fa`);
    return response.data;
  },

  blockStudent: async (codEtu, message) => {
    const response = await api.post(`/admin/students/${codEtu}/block`, { message });
    return response.data;
  },

  unblockStudent: async (codEtu) => {
    const response = await api.post(`/admin/students/${codEtu}/unblock`);
    return response.data;
  },

  getInstitutionalEmails: async (params = {}) => {
    const response = await api.get('/admin/institutional-emails', { params });
    return response.data;
  },
  importInstitutionalEmails: async (rows) => {
    const response = await api.post('/admin/institutional-emails/import', { rows });
    return response.data;
  },
  deleteInstitutionalEmail: async (id) => {
    const response = await api.delete(`/admin/institutional-emails/${id}`);
    return response.data;
  },

  searchModules: async (q) => {
    const response = await api.get(`/admin/modules/search`, { params: { q } });
    return response.data;
  },

  getModuleGroupPreview: async (codEtu, codElp) => {
    const response = await api.get(`/admin/modules/group-preview`, { params: { codEtu, codElp } });
    return response.data;
  },

  addStudentModule: async (codEtu, { cod_elp, cod_anu }) => {
    const response = await api.post(`/admin/students/${codEtu}/add-module`, { cod_elp, cod_anu });
    return response.data;
  },

  removeStudentModules: async (codEtu, modules) => {
    const response = await api.delete(`/admin/students/${codEtu}/remove-modules`, { data: { modules } });
    return response.data;
  },

  // Student Management APIs
  searchStudents: async (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) searchParams.append(key, params[key]);
    });
    
    const response = await api.get(`/admin/students/search?${searchParams}`);
    return response.data;
  },

  // NEW: Fetch detailed breakdown (Module Code -> Groups)
  getDetailedStats: async () => {
    const response = await api.get('/admin/groups/stats/full-export');
    return response.data;
  },

  getPedagoModules: async () => {
    const response = await api.get('/admin/groups/pedago-modules');
    return response.data;
  },

  getGroupStudents: async (moduleCode, groupName) => {
    const params = new URLSearchParams();
    params.append('module', moduleCode);
    if (groupName) params.append('group', groupName);
    
    const response = await api.get(`/admin/groups/students?${params}`);
    return response.data;
  },

  // Professors
  getProfessors: async () => {
    const response = await api.get('/admin/professors');
    return response.data;
  },
  addProfessor: async (name, email) => {
    const response = await api.post('/admin/professors', { name, email });
    return response.data;
  },
  updateProfessor: async (id, name, email) => {
    const response = await api.put(`/admin/professors/${id}`, { name, email });
    return response.data;
  },
  bulkImportProfessors: async (professors) => {
    const response = await api.post('/admin/professors/bulk-import', { professors });
    return response.data;
  },
  deleteProfessor: async (id) => {
    await api.delete(`/admin/professors/${id}`);
  },

  // Locations
  getLocations: async () => {
    const response = await api.get('/admin/locations');
    return response.data;
  },
  
  addLocation: async (data) => {
    const response = await api.post('/admin/locations', data);
    return response.data;
  },
  
  deleteLocation: async (id) => {
    await api.delete(`/admin/locations/${id}`);
  },

  // Updated Exam Methods
  getExamStudents: async (examId) => {
    const response = await api.get(`/admin/exams/${examId}/students`);
    return response.data;
  },

  
  // --- ADD THIS METHOD ---
  getGroupingRules: async () => {
    const response = await api.get('/admin/groups/rules');
    return response.data;
  },
  // Exam Sessions
  getExamSessions: async () => {
    const response = await api.get('/admin/exam-sessions');
    return response.data;
  },
  createExamSession: async (data) => {
    const response = await api.post('/admin/exam-sessions', data);
    return response.data;
  },
  snapshotExamSession: async (data) => {
    const response = await api.post('/admin/exam-sessions/snapshot', data);
    return response.data;
  },
  activateExamSession: async (id) => {
    const response = await api.put(`/admin/exam-sessions/${id}/activate`);
    return response.data;
  },
  archiveExamSession: async (id) => {
    const response = await api.put(`/admin/exam-sessions/${id}/archive`);
    return response.data;
  },
  deleteExamSession: async (id, force) => {
    const response = await api.delete(`/admin/exam-sessions/${id}${force ? '?force=true' : ''}`);
    return response.data;
  },
  generateRattrapageSession: async (sourceSessionId, name, startDate) => {
    const response = await api.post(`/admin/exam-sessions/${sourceSessionId}/generate-rattrapage`, { name, start_date: startDate });
    return response.data;
  },
  getRattrapageGenerationStatus: async (jobId) => {
    const response = await api.get(`/admin/exam-sessions/generate-rattrapage-status/${jobId}`);
    return response.data;
  },
  getRattrapageSituations: async (sessionId) => {
    const response = await api.get(`/admin/exam-sessions/${sessionId}/rattrapage-situations`);
    return response.data;
  },
  syncRattrapageList: async (sessionId) => {
    const response = await api.post(`/admin/exam-sessions/${sessionId}/sync-rattrapage-list`);
    return response.data;
  },
  getRattrapageModules: async (sessionId) => {
    const response = await api.get(`/admin/exam-sessions/${sessionId}/rattrapage-modules`);
    return response.data;
  },
  getRattrapageStudents: async (sessionId, moduleCodes, groupNames) => {
    const params = { module: moduleCodes };
    if (groupNames) params.group = groupNames;
    const response = await api.get(`/admin/exam-sessions/${sessionId}/rattrapage-students`, { params });
    return response.data;
  },

  // Exam Planning
  getExams: async (sessionId) => {
    const params = sessionId ? `?session_id=${sessionId}` : '';
    const response = await api.get(`/admin/exams${params}`);
    return response.data;
  },



  deleteExam: async (id) => {
    const response = await api.delete(`/admin/exams/${id}`);
    return response.data;
  },
createExam: async (examData) => {
    const response = await api.post('/admin/exams', examData);
    return response.data;
  },
updateExam: async (id, data) => {
    const response = await api.put(`/admin/exams/${id}`, data);
    return response.data;
  },


  // --- NEW METHOD ---
exportExamAssignments: async (sessionId = null) => {
    try {
      const params = sessionId ? { session_id: sessionId } : {};
      const response = await api.get('/admin/exams/export/assignments', {
        params,
        responseType: 'blob',
        timeout: 120000
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `planning_global_detaile_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  },
  exportRattrapageCF: async (year = null) => {
    const url = year
      ? `/admin/grades/rattrapage/export?year=${year}`
      : '/admin/grades/rattrapage/export';
    const response = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `rattrapage_CF${year ? `_${year}` : ''}_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  },

  // Export Students Function
  exportStudents: async (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) searchParams.append(key, params[key]);
    });
    
    const response = await api.get(`/admin/students/export?${searchParams}`, {
      responseType: 'blob' // Important for handling file downloads
    });
    
    // Trigger file download in browser
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = `students_export_${params.year || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  },

  getLaureatDetails: async (codEtu) => {
    const response = await api.get(`/admin/laureats/student/${codEtu}`);
    return response.data;
  },
  
  getEmployeeStats: async () => {
    const response = await api.get('/admin/employees/stats');
    return response.data;
  },

  // --- RH MANAGEMENT (Employees) ---
  getEmployees: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.search) params.append('search', filters.search);
    
    const response = await api.get(`/admin/employees?${params}`);
    return response.data;
  },

  addEmployee: async (employeeData) => {
    const response = await api.post('/admin/employees', employeeData);
    return response.data;
  },

  updateEmployee: async (id, employeeData) => {
    const response = await api.put(`/admin/employees/${id}`, employeeData);
    return response.data;
  },

  deleteEmployee: async (id) => {
    const response = await api.delete(`/admin/employees/${id}`);
    return response.data;
  },

  // Laureat Management
  getLaureats: async (params) => {
    const searchParams = new URLSearchParams();
    
    if (params.year) searchParams.append('year', params.year);
    if (params.diploma) searchParams.append('diploma', params.diploma);
    if (params.search) searchParams.append('search', params.search);
    if (params.page) searchParams.append('page', params.page);
    if (params.multiDiploma) searchParams.append('multiDiploma', 'true');
    
    const response = await api.get(`/admin/laureats?${searchParams.toString()}`);
    return response.data;
  },

  getLaureatStats: async () => {
    const response = await api.get('/admin/laureats/stats');
    return response.data;
  },

  syncLaureats: async (years) => {
    const response = await api.post('/admin/laureats/sync', { years });
    return response.data;
  },

  // Student Registration Management APIs
  getStudentRegistrations: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.year) searchParams.append('year', params.year);
    if (params.user) searchParams.append('user', params.user);
    if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) searchParams.append('dateTo', params.dateTo);
    if (params.limit) searchParams.append('limit', params.limit);
    
    const response = await api.get(`/admin/registrations?${searchParams}`);
    return response.data;
  },

  exportRegistrationsPDF: async (filters = {}) => {
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams();
      if (filters.year) queryParams.append('year', filters.year);
      if (filters.user) queryParams.append('user', filters.user);
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

      const url = `/admin/registrations/export-pdf?${queryParams.toString()}`;
      
      const response = await api.get(url, {
        responseType: 'text',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const newWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      if (newWindow) {
        newWindow.document.write(response.data);
        newWindow.document.close();
      } else {
        const blob = new Blob([response.data], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      }
      return { success: true, message: 'Rapport PDF généré avec succès' };
    } catch (error) {
      console.error('Error generating PDF report:', error);
      throw error;
    }
  },

  getRegistrationStats: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.year) searchParams.append('year', params.year);
    if (params.user) searchParams.append('user', params.user);
    if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) searchParams.append('dateTo', params.dateTo);
    
    const response = await api.get(`/admin/registrations/stats?${searchParams}`);
    return response.data;
  },

  exportRegistrations: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.year) searchParams.append('year', params.year);
    if (params.user) searchParams.append('user', params.user);
    if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) searchParams.append('dateTo', params.dateTo);
    
    try {
      const response = await api.get(`/admin/registrations/export?${searchParams}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `student_registrations_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  },
  
  logout: () => {
    localStorage.removeItem('adminToken');
  },
  
  verify: async () => {
    const response = await api.get('/admin/verify');
    return response.data;
  },
  
  getToken: () => {
    return localStorage.getItem('adminToken');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('adminToken');
  },
  
  // Full Statistics
  getStatistics: async (year) => {
    const params = year ? { year } : {};
    const response = await api.get('/admin/statistics', { params });
    return response.data;
  },

  // Admin Management APIs (SUPER_ADMIN only)
  getAdmins: async () => {
    const response = await api.get('/admin/manage/admins');
    return response.data;
  },
  createAdmin: async (data) => {
    const response = await api.post('/admin/manage/admins', data);
    return response.data;
  },
  updateAdmin: async (id, data) => {
    const response = await api.put(`/admin/manage/admins/${id}`, data);
    return response.data;
  },
  deleteAdmin: async (id) => {
    const response = await api.delete(`/admin/manage/admins/${id}`);
    return response.data;
  },

  // Photo APIs
  getPhotoStats: async () => {
    const response = await api.get('/admin/photos/stats');
    return response.data;
  },

  syncPhotos: async () => {
    const response = await api.post('/admin/photos/sync');
    return response.data;
  },

  getStudentPhotoUrl,

  // Dashboard APIs
  getDashboardStats: async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },
  
  getDataOverview: async () => {
    const response = await api.get('/admin/dashboard/overview');
    return response.data;
  },
  
  getStudent: async (id) => {
    const response = await api.get(`/admin/students/${id}`);
    return response.data;
  },
  
  // Sync Management APIs
  getSyncStatus: async (limit = 10) => {
    const response = await api.get(`/admin/sync/status?limit=${limit}`);
    return response.data;
  },
  
  triggerManualSync: async () => {
    const response = await api.post('/admin/sync/manual');
    return response.data;
  },
  
  // System Health APIs
  getSystemHealth: async () => {
    const response = await api.get('/admin/system/health');
    return response.data;
  },
  
  getSystemStats: async (period = 30) => {
    const response = await api.get(`/admin/system/stats?period=${period}`);
    return response.data;
  },

  // Module Management
  getModules: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.element_type) searchParams.append('element_type', params.element_type);
    if (params.semester) searchParams.append('semester', params.semester);
    if (params.parent_code) searchParams.append('parent_code', params.parent_code);
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    
    const response = await api.get(`/admin/modules?${searchParams}`);
    return response.data;
  },
  
  getModule: async (id) => {
    const response = await api.get(`/admin/modules/${id}`);
    return response.data;
  },
  
  updateModule: async (id, moduleData) => {
    const response = await api.put(`/admin/modules/${id}`, moduleData);
    return response.data;
  },
  
  updateModuleParent: async (id, parentCode) => {
    const response = await api.put(`/admin/modules/${id}/parent`, { parent_code: parentCode });
    return response.data;
  },
  
  getAvailableParents: async (search = '') => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await api.get(`/admin/modules/available-parents${params}`);
    return response.data;
  },
  
  getModuleUsage: async (id) => {
    const response = await api.get(`/admin/modules/${id}/usage`);
    return response.data;
  },
  
  bulkUpdateSemesters: async (updates) => {
    const response = await api.post('/admin/modules/bulk-update-semester', { updates: updates });
    return response.data;
  },
  
  getSemesterOptions: () => {
    return [
      { value: null, label: 'No Semester Assigned' },
      { value: 1, label: 'S1 - First Semester' },
      { value: 2, label: 'S2 - Second Semester' },
      { value: 3, label: 'S3 - Third Semester' },
      { value: 4, label: 'S4 - Fourth Semester' },
      { value: 5, label: 'S5 - Fifth Semester' },
      { value: 6, label: 'S6 - Sixth Semester' },
      { value: 7, label: 'S7 - Seventh Semester' },
      { value: 8, label: 'S8 - Eighth Semester' },
      { value: 9, label: 'S9 - Ninth Semester' },
      { value: 10, label: 'S10 - Tenth Semester' },
      { value: 11, label: 'S11 - Eleventh Semester' },
      { value: 12, label: 'S12 - Twelfth Semester' }
    ];
  },
  
  getElementTypeOptions: () => {
    return [
      { value: 'MATIERE', label: 'MATIERE - Subject/Course' },
      { value: 'MODULE', label: 'MODULE - Module' },
      { value: 'SEMESTRE', label: 'SEMESTRE - Semester/Year' },
      { value: 'ANNEE', label: 'ANNEE - Academic Year' }
    ];
  },

  getYearLevelOptions: () => {
    return [
      { value: null, label: 'No Year Assigned' },
      { value: 1, label: 'Year 1 (1ère Année)' },
      { value: 2, label: 'Year 2 (2ème Année)' },
      { value: 3, label: 'Year 3 (3ème Année)' },
      { value: 4, label: 'Year 4 (4ème Année)' },
      { value: 5, label: 'Year 5 (5ème Année)' },
      { value: 6, label: 'Year 6 (6ème Année)' }
    ];
  },
  
  formatModuleForDisplay: (module) => {
    return {
      ...module,
      semester_display: module.semester_number ? `S${module.semester_number}` : 'Not Assigned',
      year_display: module.year_level ? `Year ${module.year_level}` : 'Not Assigned',
      element_type_display: module.element_type || 'Not Set',
      usage_display: module.grade_usage_count ? `${module.grade_usage_count} grades` : 'No usage',
      children_display: module.children_count ? `${module.children_count} children` : 'No children',
      parent_display: module.parent_name || 'No parent',
      last_sync_display: module.last_sync ? new Date(module.last_sync).toLocaleDateString() : 'Never'
    };
  },
  
  validateModuleData: (moduleData) => {
    const errors = [];
    if (moduleData.semester_number !== null && moduleData.element_type !== 'ANNEE') {
      if (moduleData.semester_number < 1 || moduleData.semester_number > 12) {
        errors.push('Semester number must be between 1 and 12');
      }
    }
    if (moduleData.year_level !== null && moduleData.element_type === 'ANNEE') {
      if (moduleData.year_level < 1 || moduleData.year_level > 6) {
        errors.push('Year level must be between 1 and 6');
      }
    }
    const validTypes = ['SEMESTRE', 'MODULE', 'MATIERE', 'ANNEE'];
    if (moduleData.element_type && !validTypes.includes(moduleData.element_type)) {
      errors.push(`Invalid element type: ${moduleData.element_type}. Valid types are: ${validTypes.join(', ')}`);
    }
    if (moduleData.lib_elp && moduleData.lib_elp.trim().length === 0) {
      errors.push('Module name cannot be empty');
    }
    if (moduleData.element_type === 'ANNEE') {
      if (moduleData.semester_number !== null) errors.push('ANNEE elements should not have a semester number');
      if (moduleData.year_level === null) errors.push('ANNEE elements must have a year level');
    } else if (moduleData.element_type !== 'ANNEE') {
      if (moduleData.year_level !== null) errors.push('Only ANNEE elements can have a year level');
    }
    return { isValid: errors.length === 0, errors: errors };
  },
  
  searchModules: async (searchTerm, filters = {}) => {
    const params = {
      search: searchTerm,
      ...filters,
      limit: 20
    };
    return await adminAPI.getModules(params);
  },
  
  exportModules: async (filters = {}) => {
    const params = {
      ...filters,
      limit: 1000,
      page: 1
    };
    const response = await adminAPI.getModules(params);
    const exportData = response.modules.map(module => ({
      'Module Code': module.cod_elp,
      'Module Name': module.lib_elp,
      'Arabic Name': module.lib_elp_arb,
      'Element Type': module.element_type,
      'Semester': module.semester_number ? `S${module.semester_number}` : 'Not Assigned',
      'Parent Code': module.parent_code || 'No Parent',
      'Parent Name': module.parent_name || 'No Parent',
      'Grade Usage': module.grade_usage_count || 0,
      'Children Count': module.children_count || 0,
      'Last Sync': module.last_sync ? new Date(module.last_sync).toLocaleDateString() : 'Never'
    }));
    return exportData;
  },

  getStudentCardRequests: async () => {
    const response = await api.get('/admin/student-card-requests');
    return response.data;
  },

  getCardDocument: async (id) => {
    const response = await api.get(`/admin/student-card-requests/${id}/document`, { responseType: 'blob' });
    return response.data;
  },

  // Added Function
  updateStudentCardRequestStatus: async (id, status, comment = '') => {
    const response = await api.put(`/admin/student-card-requests/${id}`, { status, comment });
    return response.data;
  },

  // Grade reclamations
  getReclamations: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.status) params.append('status', filters.status);

    const response = await api.get(`/admin/reclamations?${params}`);
    return response.data;
  },
  updateReclamation: async (id, data) => {
    const response = await api.put(`/admin/reclamations/${id}`, data);
    return response.data;
  },
  deleteReclamation: async (id) => {
    const response = await api.delete(`/admin/reclamations/${id}`);
    return response.data;
  },
  bulkDeleteReclamations: async (ids) => {
    const response = await api.post('/admin/reclamations/bulk-delete', { ids });
    return response.data;
  },
  bulkUpdateReclamationStatus: async (ids, status) => {
    const response = await api.post('/admin/reclamations/bulk-status', { ids, status });
    return response.data;
  },
  // Exam statistics
  getExamStats: async () => {
    const response = await api.get('/admin/exam-stats');
    return response.data;
  },
  importExamGrades: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/admin/exam-stats/import-grades', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  // Reclamation module configuration
  getReclamationModules: async () => {
    const response = await api.get('/admin/reclamation-modules');
    return response.data;
  },
  updateReclamationModule: async (id, enabled) => {
    const response = await api.put(`/admin/reclamation-modules/${id}`, { enabled });
    return response.data;
  },
  bulkUpdateReclamationModules: async (sessionId, enabled) => {
    const response = await api.put('/admin/reclamation-modules/bulk', { session_id: sessionId, enabled });
    return response.data;
  },
  getSchedules: () => api.get('/admin/schedules'),
  createSchedule: (data) => api.post('/admin/schedules', data),
  deleteSchedule: (id) => api.delete(`/admin/schedules/${id}`),

  // Pedagogy Structure Manager (legacy)
  getPedagogyPrograms: () => api.get('/admin/pedagogy/programs').then(r => r.data),
  getPedagogyModules: (program, semester) => api.get(`/admin/pedagogy/modules?program=${program}&semester=${semester}`).then(r => r.data),
  getPedagogyUnassigned: (program) => api.get(`/admin/pedagogy/unassigned?program=${encodeURIComponent(program)}`).then(r => r.data),
  assignModule: (cod_elp, semester_number, year_level) => api.put('/admin/pedagogy/assign', { cod_elp, semester_number, year_level }).then(r => r.data),
  unassignModule: (cod_elp) => api.delete(`/admin/pedagogy/assign/${cod_elp}`).then(r => r.data),

  // Hierarchy Manager
  getHierarchyFilieres: () => api.get('/admin/hierarchy/filieres').then(r => r.data),
  getHierarchyModules: (cod) => api.get(`/admin/hierarchy/filieres/${cod}/modules`).then(r => r.data),
  createFiliere: (data) => api.post('/admin/hierarchy/filieres', data).then(r => r.data),
  updateFiliere: (cod, data) => api.put(`/admin/hierarchy/filieres/${cod}`, data).then(r => r.data),
  deleteFiliere: (cod) => api.delete(`/admin/hierarchy/filieres/${cod}`).then(r => r.data),
  createHierarchyModule: (data) => api.post('/admin/hierarchy/modules', data).then(r => r.data),
  updateHierarchyModule: (id, data) => api.put(`/admin/hierarchy/modules/${id}`, data).then(r => r.data),
  deleteHierarchyModule: (id) => api.delete(`/admin/hierarchy/modules/${id}`).then(r => r.data),
  bulkHierarchyModules: (ids, is_active) => api.put('/admin/hierarchy/modules/bulk', { ids, is_active }).then(r => r.data),
  getHierarchyDiff: (filiere) => api.get(`/admin/hierarchy/diff${filiere ? `?filiere=${filiere}` : ''}`).then(r => r.data),
  seedHierarchy: () => api.post('/admin/hierarchy/seed').then(r => r.data),
};

// System API
// Public (no login) endpoints
export const publicAPI = {
  submitMasterForm: (formData) => api.post('/masterform/submit', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // up to 4 × 5 MB documents on slow connections
  }).then(res => res.data),

  // Admin side — URLs contain '/admin/' so the interceptor sends the admin token
  getMasterformSubmissions: () => api.get('/masterform/admin/submissions').then(res => res.data),
  getMasterformDocument: (id, field) =>
    api.get(`/masterform/admin/submissions/${id}/document/${field}`, { responseType: 'blob' }).then(res => res.data),
  deleteMasterformSubmission: (id) => api.delete(`/masterform/admin/submissions/${id}`).then(res => res.data),
};

export const systemAPI = {
  healthCheck: async () => {
    const response = await api.get('/health');
    return response.data;
  }
};

export default api;